import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/** localStorage key for the chosen display size; index.html reads it too, before Angular boots. */
export const UI_SCALE_STORAGE_KEY = 'uiScale';

/** Display sizes offered in the user menu: Default (100%), Medium (112%), Large (125%). */
export const UI_SCALE_LEVELS = [
  { label: 'Default', scale: 1 },
  { label: 'Medium', scale: 1.12 },
  { label: 'Large', scale: 1.25 },
];

export const UI_SCALE_OPTIONS = UI_SCALE_LEVELS.map(level => level.scale);

/** Largest scale each screen width allows, so text can't outgrow the layout (keep in sync with index.html). */
export function maxUiScaleForWidth(width: number): number {
  return width < 1920 ? 1.12 : 1.25;
}

/** Display size currently applied (1 to 1.25), for charts that set px font sizes in code. */
export function getUiScale(): number {
  const value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ui-scale'));
  return value > 0 ? value : 1;
}

/**
 * Display size ("platform scale"). Sets `--ui-scale` on <html>, which the type tokens, the
 * .lato-*-vw classes and the responsive-units component sizes multiply by. Layout regions don't scale.
 */
@Injectable({ providedIn: 'root' })
export class UiScaleService implements OnDestroy {
  private preferred = this.readStored();
  private effectiveSubject = new BehaviorSubject<number>(1);
  private maxSubject = new BehaviorSubject<number>(maxUiScaleForWidth(window.innerWidth));
  private resizeSub: Subscription;

  /** Scale the user picked (may be above what the current screen allows). */
  get preferredScale(): number {
    return this.preferred;
  }

  /** Scale actually applied right now. */
  readonly effectiveScale$ = this.effectiveSubject.asObservable();
  /** Largest scale the current screen width allows. */
  readonly maxScale$ = this.maxSubject.asObservable();

  constructor(private zone: NgZone) {
    this.apply(false);
    this.zone.runOutsideAngular(() => {
      this.resizeSub = fromEvent(window, 'resize')
        .pipe(debounceTime(150))
        .subscribe(() => {
          const max = maxUiScaleForWidth(window.innerWidth);
          if (max !== this.maxSubject.value) {
            this.zone.run(() => {
              this.maxSubject.next(max);
              this.apply(true);
            });
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.resizeSub?.unsubscribe();
  }

  setScale(scale: number): void {
    if (!UI_SCALE_OPTIONS.includes(scale)) {
      return;
    }
    this.preferred = scale;
    try {
      if (scale === 1) {
        localStorage.removeItem(UI_SCALE_STORAGE_KEY);
      } else {
        localStorage.setItem(UI_SCALE_STORAGE_KEY, String(scale));
      }
    } catch {
      // storage unavailable - the scale still applies for this session
    }
    this.apply(true);
  }

  private apply(notifyLayout: boolean): void {
    const effective = Math.min(this.preferred, maxUiScaleForWidth(window.innerWidth));
    // compare with what is on <html>: index.html (or a stale value) may have set something else
    const applied = parseFloat(document.documentElement.style.getPropertyValue('--ui-scale'));
    if (effective === this.effectiveSubject.value && applied === effective) {
      return;
    }
    document.documentElement.style.setProperty('--ui-scale', String(effective));
    this.effectiveSubject.next(effective);
    // charts, virtual-scroll row heights and column fills measure themselves on resize
    if (notifyLayout) {
      requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    }
  }

  private readStored(): number {
    try {
      const stored = Number(localStorage.getItem(UI_SCALE_STORAGE_KEY));
      return UI_SCALE_OPTIONS.includes(stored) ? stored : 1;
    } catch {
      return 1;
    }
  }
}
